import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, IconButton, Chip, CircularProgress, Box, Alert,
  Stack, Divider
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack'; 

import { 
  getAsyncAnonymizationJobs, 
  downloadAsyncAnonymizedFile,
  deleteAsyncAnonymizationJob
} from '../../../services/asyncAnonymizationService';

import DeleteConfirmationModal from '../../../shared/DeleteConfirmationModal/DeleteConfirmationModal';
import PreviewPDFEdit from '../PIIFileUploadPanel/PreviewPDFEdit/PreviewPDFEdit'; 

const AsyncJobsList = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar(); 

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [fetchingEditId, setFetchingEditId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [previewJob, setPreviewJob] = useState(null);

  const fetchJobs = async () => {
    try {
      const data = await getAsyncAnonymizationJobs();
      setJobs((prevJobs) => data.map(newJob => {
        const existingJob = prevJobs.find(pj => pj.id === newJob.id);
        return existingJob?.blob ? { ...newJob, blob: existingJob.blob } : newJob;
      }));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleDownload = async (jobId, originalFilename) => {
    setDownloadingId(jobId);
    try {
      const blob = await downloadAsyncAnonymizedFile(jobId);
      setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, blob } : j)));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `anonymized_${originalFilename}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      enqueueSnackbar(t('AsyncJobsList.downloadFailed', 'Stiahnutie zlyhalo: {{errorMessage}}', { errorMessage: err.message }), { variant: 'error' });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOpenEdit = async (job) => {
    if (job.blob) {
      setPreviewJob({ id: job.id, name: job.input_filename, blob: job.blob });
      return;
    }
    setFetchingEditId(job.id);
    try {
      const blob = await downloadAsyncAnonymizedFile(job.id);
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, blob } : j)));
      setPreviewJob({ id: job.id, name: job.input_filename, blob });
    } catch (err) {
      enqueueSnackbar(t('AsyncJobsList.fetchFailed', 'Nepodarilo sa načítať súbor pre úpravu: {{errorMessage}}', { errorMessage: err.message }), { variant: 'error' });
    } finally {
      setFetchingEditId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedJob) return;
    try {
      await deleteAsyncAnonymizationJob(selectedJob.id);
      setJobs((prev) => prev.filter((j) => j.id !== selectedJob.id));
      enqueueSnackbar(t('AsyncJobsList.deleteSuccess', 'Záznam bol úspešne vymazaný.'), { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(t('AsyncJobsList.deleteFailed', 'Odstránenie zlyhalo: {{errorMessage}}', { errorMessage: err.message }), { variant: 'error' });
    } finally {
      setDeleteModalOpen(false);
      setSelectedJob(null);
    }
  };

  const getStatusChip = (status) => {
    const commonSx = { borderRadius: '6px', fontWeight: 600 };
    switch (status) {
      case 'done': return <Chip label={t('AsyncJobsList.status.done', 'Hotovo')} color="success" size="small" variant="outlined" sx={commonSx} />;
      case 'running': return <Chip label={t('AsyncJobsList.status.running', 'Spracúva sa')} color="primary" size="small" variant="outlined" sx={commonSx} />;
      case 'pending': return <Chip label={t('AsyncJobsList.status.pending', 'V rade')} color="warning" size="small" variant="outlined" sx={commonSx} />;
      case 'failed': return <Chip label={t('AsyncJobsList.status.failed', 'Chyba')} color="error" size="small" variant="outlined" sx={commonSx} />;
      default: return <Chip label={status} size="small" variant="outlined" sx={commonSx} />;
    }
  };

  const isRowBusy = (jobId) => downloadingId === jobId || fetchingEditId === jobId;

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
        <HistoryIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('AsyncJobsList.title', 'História spracovania')}</Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {loading && jobs.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : jobs.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'action.selected', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
          <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="subtitle1" fontWeight={600}>{t('AsyncJobsList.emptyTitle', 'Zatiaľ žiadna história')}</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ flexGrow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>{t('AsyncJobsList.table.fileName', 'Súbor')}</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>{t('AsyncJobsList.table.date', 'Dátum')}</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>{t('AsyncJobsList.table.status', 'Stav')}</TableCell>
                <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>{t('AsyncJobsList.table.action', 'Akcia')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} hover sx={{ '& td': { borderBottomColor: 'divider' } }}>
                  <TableCell><Typography variant="body2" fontWeight={500}>{job.input_filename}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2">{new Date(job.created_at).toLocaleDateString('sk-SK')}</Typography>
                    <Typography variant="caption" color="text.secondary">{new Date(job.created_at).toLocaleTimeString('sk-SK')}</Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(job.status)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" color="primary" disabled={job.status !== 'done' || isRowBusy(job.id)} onClick={() => handleOpenEdit(job)}>
                        {fetchingEditId === job.id ? <CircularProgress size={20} /> : <EditIcon fontSize="small" />}
                      </IconButton>
                      <Button variant={job.status === 'done' ? "contained" : "outlined"} size="small" startIcon={<DownloadIcon />} disabled={job.status !== 'done' || isRowBusy(job.id)} onClick={() => handleDownload(job.id, job.input_filename)}>
                        {downloadingId === job.id ? '...' : t('AsyncJobsList.button.download', 'Stiahnuť')}
                      </Button>
                      <IconButton size="small" color="error" onClick={() => { setSelectedJob(job); setDeleteModalOpen(true); }}><CloseIcon fontSize="small" /></IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <DeleteConfirmationModal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={handleConfirmDelete} />
      {previewJob && <PreviewPDFEdit open={!!previewJob} fileItem={previewJob} onClose={() => setPreviewJob(null)} />}
    </Paper>
  );
};

export default AsyncJobsList;